import {
  CODE_COOLDOWN_OFFSET,
  CODE_COOLDOWN_STARTED_COOKIE,
} from './EmailConfirmationModal.constants';

export type EmailConfirmationError = string | { message: string };

export type EmailConfirmationResult = {
  entity: number | null;
  errors: EmailConfirmationError[];
};

export type EmailConfirmationFieldName =
  | 'createEmailConfirmation'
  | 'confirmEmailConfirmation';

export function formatTime(totalSeconds: number) {
  const minutes = Math.floor(totalSeconds / 60);
  const seconds = totalSeconds % 60;

  return `${String(minutes).padStart(2, '0')}:${String(seconds).padStart(
    2,
    '0'
  )}`;
}

export function getCodeCooldownDuration(codeLifetime: number) {
  return codeLifetime - CODE_COOLDOWN_OFFSET;
}

function getRecordProperty(value: unknown, property: string) {
  if (!value || typeof value !== 'object') return null;

  return (value as Record<string, unknown>)[property];
}

export function getEmailConfirmationResultFromError(
  error: unknown,
  fieldName: EmailConfirmationFieldName
): EmailConfirmationResult | null {
  const networkError = getRecordProperty(error, 'networkError') ?? error;
  const result = getRecordProperty(networkError, 'result');
  const data = getRecordProperty(result, 'data');
  const field = getRecordProperty(data, fieldName);
  const entity = getRecordProperty(field, 'entity');
  const errors = getRecordProperty(field, 'errors');

  if (typeof entity !== 'number' && entity !== null) return null;
  if (!Array.isArray(errors)) return null;

  return {
    entity,
    errors: errors as EmailConfirmationError[],
  };
}

export function getFirstEmailConfirmationErrorMessage(
  errors: EmailConfirmationError[] | undefined,
  fallbackMessage: string
) {
  const firstError = errors?.[0];

  if (typeof firstError === 'string') return firstError;

  return firstError?.message ?? fallbackMessage;
}

function getCookieDomain() {
  if (typeof window === 'undefined') return null;

  const host = window.location.hostname;
  if (!host) return null;

  return `.${host.replace(/^ru\./, '')}`;
}

function writeCookie(name: string, value: string, maxAgeSeconds?: number) {
  if (typeof document === 'undefined') return;

  const secure = window.location.protocol === 'https:' ? '; Secure' : '';
  const domain = getCookieDomain();
  const domainAttr = domain ? `; Domain=${domain}` : '';
  const maxAgeAttr =
    maxAgeSeconds === undefined ? '' : `; Max-Age=${maxAgeSeconds}`;

  document.cookie = `${encodeURIComponent(name)}=${encodeURIComponent(
    value
  )}; Path=/; SameSite=Lax${maxAgeAttr}${secure}${domainAttr}`;
}

export function loadCodeCooldownStarted() {
  if (typeof document === 'undefined') return false;

  const encodedName = `${encodeURIComponent(CODE_COOLDOWN_STARTED_COOKIE)}=`;
  const cookie = document.cookie
    .split(';')
    .map(part => part.trim())
    .find(part => part.startsWith(encodedName));

  return !!cookie?.slice(encodedName.length);
}

export function saveCodeCooldownStarted(value: boolean) {
  writeCookie(
    CODE_COOLDOWN_STARTED_COOKIE,
    value ? '1' : '',
    value ? undefined : 0
  );
}
